import i18n from "../i18n";

/** ISO 3166-1 alpha-2 codes - the same set the backend accepts
 * (`common.utils.get_country_choices`, pycountry). Addresses store the code;
 * the UI always shows the name (`countryName`). */
export const COUNTRY_CODES = [
  "AD", "AE", "AF", "AG", "AI", "AL", "AM", "AO", "AQ", "AR", "AS", "AT", "AU", "AW", "AX", "AZ",
  "BA", "BB", "BD", "BE", "BF", "BG", "BH", "BI", "BJ", "BL", "BM", "BN", "BO", "BQ", "BR", "BS",
  "BT", "BV", "BW", "BY", "BZ", "CA", "CC", "CD", "CF", "CG", "CH", "CI", "CK", "CL", "CM", "CN",
  "CO", "CR", "CU", "CV", "CW", "CX", "CY", "CZ", "DE", "DJ", "DK", "DM", "DO", "DZ", "EC", "EE",
  "EG", "EH", "ER", "ES", "ET", "FI", "FJ", "FK", "FM", "FO", "FR", "GA", "GB", "GD", "GE", "GF",
  "GG", "GH", "GI", "GL", "GM", "GN", "GP", "GQ", "GR", "GS", "GT", "GU", "GW", "GY", "HK", "HM",
  "HN", "HR", "HT", "HU", "ID", "IE", "IL", "IM", "IN", "IO", "IQ", "IR", "IS", "IT", "JE", "JM",
  "JO", "JP", "KE", "KG", "KH", "KI", "KM", "KN", "KP", "KR", "KW", "KY", "KZ", "LA", "LB", "LC",
  "LI", "LK", "LR", "LS", "LT", "LU", "LV", "LY", "MA", "MC", "MD", "ME", "MF", "MG", "MH", "MK",
  "ML", "MM", "MN", "MO", "MP", "MQ", "MR", "MS", "MT", "MU", "MV", "MW", "MX", "MY", "MZ", "NA",
  "NC", "NE", "NF", "NG", "NI", "NL", "NO", "NP", "NR", "NU", "NZ", "OM", "PA", "PE", "PF", "PG",
  "PH", "PK", "PL", "PM", "PN", "PR", "PS", "PT", "PW", "PY", "QA", "RE", "RO", "RS", "RU", "RW",
  "SA", "SB", "SC", "SD", "SE", "SG", "SH", "SI", "SJ", "SK", "SL", "SM", "SN", "SO", "SR", "SS",
  "ST", "SV", "SX", "SY", "SZ", "TC", "TD", "TF", "TG", "TH", "TJ", "TK", "TL", "TM", "TN", "TO",
  "TR", "TT", "TV", "TW", "TZ", "UA", "UG", "UM", "US", "UY", "UZ", "VA", "VC", "VE", "VG", "VI",
  "VN", "VU", "WF", "WS", "YE", "YT", "ZA", "ZM", "ZW",
];

/** The default country of every address form, listed first in the select. */
export const DEFAULT_COUNTRY = "PL";

export interface CountryOption {
  value: string;
  label: string;
}

const displayNamesCache = new Map<string, Intl.DisplayNames | null>();

const getDisplayNames = (lang: string): Intl.DisplayNames | null => {
  if (!displayNamesCache.has(lang)) {
    let displayNames: Intl.DisplayNames | null = null;
    try {
      displayNames = new Intl.DisplayNames([lang], { type: "region" });
    } catch {
      displayNames = null;
    }
    displayNamesCache.set(lang, displayNames);
  }
  return displayNamesCache.get(lang) ?? null;
};

/** The country's name in the UI language ("PL" -> "Polska"); the code itself
 * if the browser doesn't know it. Empty for an empty code. */
export const countryName = (code?: string | null, lang: string = i18n.language): string => {
  if (!code) return "";
  const upper = code.toUpperCase();
  try {
    return getDisplayNames(lang)?.of(upper) || upper;
  } catch {
    return upper;
  }
};

const optionsCache = new Map<string, CountryOption[]>();

/** Every country, sorted by name, with Poland first. */
export const getCountryOptions = (lang: string = i18n.language): CountryOption[] => {
  if (!optionsCache.has(lang)) {
    const others = COUNTRY_CODES.filter((code) => code !== DEFAULT_COUNTRY)
      .map((code) => ({ value: code, label: countryName(code, lang) }))
      .sort((a, b) => a.label.localeCompare(b.label, lang));
    optionsCache.set(lang, [
      { value: DEFAULT_COUNTRY, label: countryName(DEFAULT_COUNTRY, lang) },
      ...others,
    ]);
  }
  return optionsCache.get(lang)!;
};
